// react
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
// hooks
import { useLocation } from 'react-router-dom';
// @mui
import { Box, CircularProgress } from '@mui/material';
// hooks
import useAuth from '../hooks/useAuth';
// utils
import { MEMBERSHIPS } from '../config';
import { canAccessModule } from '../utils/canAccess';
// components
import BlockPerk from '../components/BlockPerk';
// ----------------------------------------------------------------------

MemershipGuard.propTypes = {
    children: PropTypes.node,
};

/**
 * Gate protected tools (tarot daily, bach full, etc.) on perk membership.
 *
 * When the user enters a protected route, we kick a live /user/me/
 * refresh to catch server-side state changes the client hasn't seen yet
 * (a subscription that just activated via PayPal webhook, or a perk that
 * expired while the tab was open). One call per tool entry — deliberately
 * not on every navigation — since only protected tools care.
 *
 * While the refresh is in flight we render a small spinner instead of
 * flashing BlockPerk for users who actually have access.
 */
export default function MemershipGuard({ children }) {
    const { pathname } = useLocation();
    const { user } = useSelector((state) => state.auth);
    const { refreshUser } = useAuth();
    const [refreshing, setRefreshing] = useState(false);
    const lastRefreshedPath = useRef(null);

    const isMembership = MEMBERSHIPS.find(m => pathname.includes(m.name));

    useEffect(() => {
        if (!isMembership) return;
        if (lastRefreshedPath.current === pathname) return;
        lastRefreshedPath.current = pathname;
        if (!refreshUser) return;
        setRefreshing(true);
        refreshUser()
            .catch(() => { /* swallow — fall back to cached state */ })
            .finally(() => setRefreshing(false));
    }, [isMembership, pathname, refreshUser]);

    if (isMembership && refreshing && !canAccessModule(user, isMembership.perk)) {
        return (
            <Box sx={{ display: 'grid', placeContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isMembership && !canAccessModule(user, isMembership.perk))
        return <BlockPerk perk={isMembership.perk} />

    return <>{children}</>;
}
